import * as React from "react";
import { $, merge } from "glamor";
import { CheckboxList } from "./checkbox-list";
import { Dropdown } from "./dropdown";
import { formatMessage } from "lib/globalize-util";
import theme from "styles/theme";
import { connect } from "react-redux";
import { IStore } from "reducers";
import { ISizeRange, setSizeRangeLabel, fetchSizeRanges, resetSizeRanges } from "actions/collections";
import _ from "lodash";
import CircularProgress from "material-ui/CircularProgress";
import { areAllItemsContainedInList } from "helpers/objUtils";
import { EmptyMessage } from "./empty-message";
import { ISize } from "../interfaces/dropdown-sizes-types";
import { BLANK } from "./checkbox-list/checkbox-list-new-item";
import { withRouter } from "react-router";
import { parseSelectedSizes, joinSelectedSizes } from "helpers/product-collection";
import { CollectionKeys, IField } from "constants/product-collection";
import { List } from "immutable";
import { SizeActions } from "constants/analytics";
import * as PropTypes from "prop-types";

interface IReduxProps {
    sizeRanges?: ISizeRange[];
    sizeRangeNotFound?: string;
    itemDisplayProperty?: List<IField>;
    isFetching?: boolean;
}

interface IDispatchProps {
    onFetchSizeRanges: () => void;
    onSetSizeRange: (sizeRangeLabel: string) => void;
    resetSizeRanges: () => void;
}

interface IProps {
    value: string;
    missingDependencies: string[];
    onChange: (value: string, sizeRanges?: ISizeRange[]) => void;
    merchantId: string;
    delimiter: string;
    itemId: string;
    defaultSelectedSizeRangeLabel?: string;
    eGridCell: HTMLElement;
    shouldClearState?: boolean;
}

interface IDropdownSizesContainerProps extends IProps {
    params: {
        id: string;
    };
}

const getSelectedSizes = (sizes: ISize[]): { allSizes: string[]; sizesAddedManually: string[] } => {
    const result = sizes.reduce(
        (accumalator, size) => {
            if (size.selected) {
                accumalator.allSizes.add(size.title);
                if (size.wasAdded) {
                    accumalator.sizesAddedManually.add(size.title);
                }
            }
            return accumalator;
        },
        { allSizes: new Set(), sizesAddedManually: new Set() }
    );
    return {
        allSizes: Array.from<string>(result.allSizes as any),
        sizesAddedManually: Array.from<string>(result.sizesAddedManually as any),
    };
};

const PAPER_HEIGHT = 250;

export const DropdownSizesContainer: React.FC<IDropdownSizesContainerProps & IReduxProps & IDispatchProps> = (
    {
        value,
        onChange,
        delimiter,
        onFetchSizeRanges,
        itemId,
        sizeRanges,
        sizeRangeNotFound,
        defaultSelectedSizeRangeLabel,
        onSetSizeRange,
        eGridCell,
        missingDependencies,
        itemDisplayProperty,
        shouldClearState,
        resetSizeRanges,
        isFetching,
    },
    context
) => {
    const cellPosition = eGridCell ? eGridCell.getBoundingClientRect() : { height: 70, y: 0 };
    const styles = React.useMemo(
        () =>
            merge(
                {
                    minWidth: 200,
                },
                $(" .paper", {
                    maxHeight: `${PAPER_HEIGHT}px`,
                    margin: "3px 0 20px 0",
                    overflow: "auto",
                }),
                $(" .MuiFormGroup-root", {
                    padding: "10px",
                }),
                $(" .MuiFormControl-root", {
                    marginTop: "15px",
                    marginBottom: "-3px",
                }),
                $(" .loading", {
                    display: "flex",
                    alignItems: "center",
                    alignContent: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    height: `${PAPER_HEIGHT + cellPosition.height}px`,
                }),
                $(" .addSize", {
                    color: theme.palette.primary1Color,
                    display: "block",
                    textDecoration: "underline",
                    outline: 0,
                    margin: "20px 0 0 20px",
                    border: "none",
                    backgroundColor: "transparent",
                }),
                $(".missingDependenciesContainer", {
                    marginBottom: "20px",
                })
            ),
        [cellPosition.height]
    );

    React.useEffect(() => {
        onFetchSizeRanges();
    }, [itemId]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (shouldClearState) {
            resetSizeRanges();
        }
    }, [shouldClearState, resetSizeRanges]);

    const paperRef = React.useRef<HTMLDivElement>();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const { allSizes: selectedSizes } = React.useMemo(() => parseSelectedSizes(value, delimiter), [value]);

    const sizeRangesOptions = React.useMemo(() => {
        if (!sizeRanges) {
            return [];
        }
        return sizeRanges.map((sizeRange) => ({ ...sizeRange, value: sizeRange.label }));
    }, [sizeRanges]);

    const [selectedSizeRangeLabel, setSelectedSizeRangeLabel] = React.useState<string>(defaultSelectedSizeRangeLabel);
    const selectedSizeRangeLabelRef = React.useRef<string>(defaultSelectedSizeRangeLabel);
    const selectedSizeRange: ISizeRange = React.useMemo(() => {
        let sr;
        if (selectedSizeRangeLabel) {
            sr = sizeRangesOptions.find((sizeRange) => sizeRange.label === selectedSizeRangeLabel);
        }
        if (!sr && selectedSizes.length > 0) {
            sr = sizeRangesOptions.find((sizeRange) => areAllItemsContainedInList(selectedSizes, sizeRange.sizes));
        }

        return sr;
    }, [selectedSizes, sizeRangesOptions, selectedSizeRangeLabel]);

    React.useEffect(() => {
        if (
            // Reset sizes if we do not find a matching size range
            (selectedSizeRange === undefined && sizeRangesOptions.length > 0 && selectedSizes.length > 0) ||
            // If the select changes, select all values
            selectedSizeRangeLabelRef.current !== selectedSizeRangeLabel
        ) {
            onChange(joinSelectedSizes(selectedSizeRange?.sizes ?? [], [], delimiter), sizeRangesOptions);
        }

        // save off last size range label
        selectedSizeRangeLabelRef.current = selectedSizeRangeLabel;
    }, [selectedSizes, selectedSizeRange, selectedSizeRangeLabelRef, selectedSizeRangeLabel, sizeRangesOptions]); // eslint-disable-line react-hooks/exhaustive-deps

    const sizes = React.useMemo(() => {
        const userSizes = selectedSizes.concat();
        const combinedSizes: ISize[] = (_.get(selectedSizeRange, ["sizes"], []) as string[]).map((size) => {
            const foundIndex = userSizes.indexOf(size);
            if (foundIndex !== -1) {
                userSizes.splice(foundIndex, 1);
            }
            return {
                id: size,
                title: size,
                selected: foundIndex !== -1,
            };
        });
        userSizes.forEach((sizeName) => {
            combinedSizes.push({
                id: sizeName as string | number,
                title: sizeName as string,
                selected: true,
                wasAdded: true,
                editable: sizeName === "" || sizeName === BLANK,
            });
        });
        return combinedSizes;
    }, [selectedSizes, selectedSizeRange]);

    const handleChangeItems = React.useCallback(
        (newItems: ISize[]) => {
            const { allSizes, sizesAddedManually } = getSelectedSizes(newItems);
            onChange(joinSelectedSizes(allSizes, sizesAddedManually, delimiter), sizeRangesOptions);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sizeRangesOptions, selectedSizeRangeLabel]
    );

    const handleItemAdded = React.useCallback(() => {
        paperRef.current.scrollTop = paperRef.current?.scrollHeight ?? 0;
    }, []);

    const handleNewItemCreated = React.useCallback(() => {
        const { allSizes: newSizes, sizesAddedManually } = getSelectedSizes(sizes);
        if (newSizes.length > 0 && (newSizes[newSizes.length - 1] === "" || newSizes[newSizes.length - 1] === BLANK)) {
            return;
        }
        newSizes.push(BLANK);
        onChange(joinSelectedSizes(newSizes, [...sizesAddedManually, BLANK], delimiter), sizeRangesOptions);
        const analytics = (global as any).analytics as any;
        analytics.track(SizeActions.SizeUnitsChanged, {
            value: newSizes.concat(sizesAddedManually).filter((x) => x !== BLANK),
            previousCount: newSizes.concat(sizesAddedManually).filter((x) => x !== BLANK).length,
        });
        paperRef.current.scrollTop = 0;
    }, [sizes, sizeRangesOptions, selectedSizeRangeLabel]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChangeSizeRange = React.useCallback((newSelectedSizeRange?: ISizeRange) => {
        const newSelectedSizeRangeLabel = newSelectedSizeRange ? newSelectedSizeRange.label : undefined;
        onSetSizeRange(newSelectedSizeRangeLabel);
        setSelectedSizeRangeLabel(newSelectedSizeRangeLabel);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (missingDependencies.length > 0) {
        const fields = missingDependencies.map((dep: string) => {
            const match = itemDisplayProperty.find((field) => field.get("field_name") === dep);
            return match ? match.get("display_name") : dep;
        });
        return (
            <div {...styles} className="missingDependenciesContainer">
                <EmptyMessage
                    message={formatMessage(
                        'Please enter |requiredFields| for this line item and then click "Find Size Ranges".',
                        {
                            requiredFields: fields.join(", "),
                        }
                    )}
                />
            </div>
        );
    }

    if (isFetching) {
        return (
            <div {...styles}>
                <div className="loading">{context.muiTheme && <CircularProgress />}</div>
            </div>
        );
    }

    return (
        <div {...styles}>
            {!sizeRangeNotFound && (
                <Dropdown
                    options={sizeRangesOptions}
                    onChange={handleChangeSizeRange}
                    defaultValue={selectedSizeRange}
                    placeholder={formatMessage("Please select range")}
                />
            )}
            <div className="paper" ref={paperRef}>
                {!sizeRangeNotFound && sizes.length === 0 ? (
                    <EmptyMessage
                        message={formatMessage(
                            'Click "Find Size Ranges" to automatically find size range else please select manually from above.'
                        )}
                    />
                ) : (
                    <>
                        {sizeRangeNotFound && (
                            <EmptyMessage message={sizeRangeNotFound || formatMessage("No Size Ranges found")} />
                        )}
                        {sizes.length > 0 && (
                            <CheckboxList
                                items={sizes}
                                onlyManualSizes={!!sizeRangeNotFound}
                                onChangeItems={handleChangeItems}
                                onItemAdded={handleItemAdded}
                            />
                        )}
                        <button className="addSize" onClick={handleNewItemCreated}>
                            {formatMessage("+ Add size")}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

DropdownSizesContainer.contextTypes = {
    muiTheme: PropTypes.object.isRequired,
};

const mapStateToProps = (state: IStore) => {
    const {
        collection: { collectionId },
        mutate,
    } = state;

    const currentCollection = mutate.snapshots && mutate.snapshots.get(collectionId);

    const itemDisplayProperty = currentCollection
        ? currentCollection.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.KEYS)
        : undefined;

    return {
        sizeRanges: state.collectionSizing.result,
        isFetching: state.collectionSizing.loading,
        sizeRangeNotFound: state.collectionSizing.error,
        itemDisplayProperty,
    };
};

const mapDispatchToProps = (dispatch, { merchantId, itemId, params }) => ({
    onFetchSizeRanges: () => dispatch(fetchSizeRanges({ itemId, merchantId, collectionId: params.id })),
    onSetSizeRange: (sizeRangeLabel: string) => dispatch(setSizeRangeLabel({ itemId, sizeRangeLabel })),
    resetSizeRanges: () => dispatch(resetSizeRanges()),
});

export default withRouter<IProps>(
    connect<IReduxProps, IDispatchProps, IDropdownSizesContainerProps>(
        mapStateToProps,
        mapDispatchToProps
    )(DropdownSizesContainer)
);
